/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Masonry from 'react-masonry-component';
import classNames from 'classnames';
import usePreloadImages from 'hooks/usePreloadImages';

import ImageOverlay from './ImageOverlay/ImageOverlay';

import ImgixImage from './../ImgixImage/ImgixImage';
import Illustration from 'gfx/icons/expand.svg';

import styles from './GalleryGrid.module.scss';

const masonryOptions = {
  transitionDuration: 0,
  itemSelector: `.${styles.item}`,
  columnWidth: `.${styles.sizer}`,
  gutter: `.${styles.gutter}`,
  horizontalOrder: true,
  // initLayout: false,
};

const imagesLoadedOptions = {};

/**
 * This is the GalleryGrid component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function GalleryGrid({ fill, items, labels }) {
  const preloader = usePreloadImages(items);
  const [openIndex, setOpenIndex] = useState(false);

  if (!preloader.done) {
    return null;
  }

  const handleClose = () => {
    setOpenIndex(false);
  };

  const childElements = items.map((item, index) => {
    return (
      <figure
        key={index}
        className={classNames(styles.item, { [styles.itemWide]: item.wide })}
      >
        <div className={styles.imageWrapper}>
          <ImgixImage {...item} eager onClick={() => setOpenIndex(index)} />
          <button
            className={styles.zoom}
            onClick={() => setOpenIndex(index)}
            aria-label={labels.btnExpand}
          >
            <Illustration role="presentation" />
          </button>
        </div>
        {item.caption && (
          <figcaption className={styles.caption}>{item.caption}</figcaption>
        )}
      </figure>
    );
  });

  return (
    <>
      <div
        className={classNames(styles.main, 'content-block', {
          [styles.fill]: fill,
        })}
      >
        <div
          className={classNames(styles.masonryWrapper, {
            ['content-width']: fill,
          })}
        >
          <div>
            <Masonry
              className={classNames(styles.masonry)}
              elementType={'div'}
              options={masonryOptions}
              disableImagesLoaded={false}
              updateOnEachImageLoad={false}
              imagesLoadedOptions={imagesLoadedOptions}
            >
              <div className={styles.sizer} />
              <div className={styles.gutter} />
              {childElements}
            </Masonry>
          </div>
        </div>
      </div>
      <ImageOverlay
        visible={openIndex !== false}
        item={items[openIndex] ?? null}
        onClose={handleClose}
      />
    </>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
GalleryGrid.propTypes = {
  fill: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape),
  labels: PropTypes.shape({
    btnExpand: PropTypes.string,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
GalleryGrid.defaultProps = {
  fill: false,
  items: [],
  labels: {
    btnExpand: 'Foo',
  },
};

export default GalleryGrid;
